/**
 * The canonical cuisine list.
 *
 * Before this, `cuisines` was whatever the publisher's JSON-LD said: 980 of
 * 1447 catalogue recipes had none, 428 carried a diet ("Sans gluten, sans
 * produits laitiers"), and the rest mixed "American" / "american" / "Thai
 * Inspired" / "indian, indonesian, malaysian, thai". None of that can be
 * scored, so none of it could be learned from.
 *
 * One main cuisine per recipe, from this list. Broad buckets ("Mediterranean",
 * "Asian", "European") are left out on purpose: they hide the real cuisine and
 * cannot be judged right or wrong.
 *
 * `everyday` is the answer for a dish with no particular cuisine (a lentil
 * soup, a chickpea noodle soup). It is a REVIEW RESULT, never written into
 * `cuisines` — search, taste matching and the planner's "same cuisine twice"
 * rule must not see a fake cuisine. It lives on `metadataReview.cuisine`.
 */

export const CANONICAL_CUISINES = [
  'quebecois',
  'french',
  'italian',
  'spanish',
  'greek',
  'middle-eastern',
  'north-african',
  'indian',
  'chinese',
  'japanese',
  'korean',
  'thai',
  'vietnamese',
  'mexican',
  'latin-american',
  'caribbean',
  'american',
  'eastern-european',
] as const;

export type CanonicalCuisine = (typeof CANONICAL_CUISINES)[number];

export const EVERYDAY_CUISINE = 'everyday' as const;

/** What a reviewer (or the model) may answer: a cuisine, or "none in particular". */
export type CuisineChoice = CanonicalCuisine | typeof EVERYDAY_CUISINE;

export const CUISINE_CHOICES: readonly CuisineChoice[] = [...CANONICAL_CUISINES, EVERYDAY_CUISINE];

export function isCanonicalCuisine(value: unknown): value is CanonicalCuisine {
  return typeof value === 'string' && (CANONICAL_CUISINES as readonly string[]).includes(value);
}

export function isCuisineChoice(value: unknown): value is CuisineChoice {
  return value === EVERYDAY_CUISINE || isCanonicalCuisine(value);
}

/** Lowercase, strip accents and punctuation. "Québécoise" → "quebecoise". */
function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z\s-]/g, ' ')
    .replace(/[\s-]+/g, ' ')
    .trim();
}

/**
 * Multi-word phrases first: they must win over their own words
 * ("italian american" is Italian food, not two cuisines; "tex mex" is not
 * American).
 */
const PHRASES: Array<[string, CanonicalCuisine]> = [
  ['italian american', 'italian'],
  ['tex mex', 'mexican'],
  ['french canadian', 'quebecois'],
  ['canadienne francaise', 'quebecois'],
  ['middle eastern', 'middle-eastern'],
  ['moyen orient', 'middle-eastern'],
  ['moyen orientale', 'middle-eastern'],
  ['north african', 'north-african'],
  ['nord africaine', 'north-african'],
  ['afrique du nord', 'north-african'],
  ['latin american', 'latin-american'],
  ['latino americaine', 'latin-american'],
  ['amerique latine', 'latin-american'],
  ['eastern european', 'eastern-european'],
  ['europe de l est', 'eastern-european'],
  ['soul food', 'american'],
  ['new england', 'american'],
  ['puerto rican', 'caribbean'],
  ['south indian', 'indian'],
  ['north indian', 'indian'],
];

/** Single words, EN and FR, adjective and country forms. */
const WORDS: Record<string, CanonicalCuisine> = {
  quebecois: 'quebecois', quebecoise: 'quebecois', quebec: 'quebecois',
  canadian: 'quebecois', canadienne: 'quebecois', canadien: 'quebecois',
  french: 'french', francaise: 'french', francais: 'french', provencal: 'french',
  provencale: 'french', bretonne: 'french', alsacienne: 'french', lyonnaise: 'french',
  italian: 'italian', italienne: 'italian', italien: 'italian', sicilian: 'italian',
  tuscan: 'italian', roman: 'italian', neapolitan: 'italian', sicilienne: 'italian',
  spanish: 'spanish', espagnole: 'spanish', espagnol: 'spanish', catalan: 'spanish',
  basque: 'spanish', andalusian: 'spanish',
  greek: 'greek', grecque: 'greek', grec: 'greek',
  lebanese: 'middle-eastern', libanaise: 'middle-eastern', libanais: 'middle-eastern',
  persian: 'middle-eastern', iranian: 'middle-eastern', persane: 'middle-eastern',
  turkish: 'middle-eastern', turque: 'middle-eastern', israeli: 'middle-eastern',
  israelienne: 'middle-eastern', syrian: 'middle-eastern', syrienne: 'middle-eastern',
  palestinian: 'middle-eastern', jordanian: 'middle-eastern', levantine: 'middle-eastern',
  moroccan: 'north-african', marocaine: 'north-african', marocain: 'north-african',
  tunisian: 'north-african', tunisienne: 'north-african', algerian: 'north-african',
  algerienne: 'north-african', egyptian: 'north-african', egyptienne: 'north-african',
  maghrebine: 'north-african',
  indian: 'indian', indienne: 'indian', indien: 'indian', punjabi: 'indian',
  goan: 'indian', keralan: 'indian',
  chinese: 'chinese', chinoise: 'chinese', chinois: 'chinese', cantonese: 'chinese',
  sichuan: 'chinese', szechuan: 'chinese', hunan: 'chinese', taiwanese: 'chinese',
  japanese: 'japanese', japonaise: 'japanese', japonais: 'japanese',
  korean: 'korean', coreenne: 'korean', coreen: 'korean',
  thai: 'thai', thaie: 'thai', thailandaise: 'thai',
  vietnamese: 'vietnamese', vietnamienne: 'vietnamese', vietnamien: 'vietnamese',
  mexican: 'mexican', mexicaine: 'mexican', mexicain: 'mexican',
  peruvian: 'latin-american', peruvienne: 'latin-american', brazilian: 'latin-american',
  bresilienne: 'latin-american', argentinian: 'latin-american', argentine: 'latin-american',
  colombian: 'latin-american', colombienne: 'latin-american', salvadoran: 'latin-american',
  venezuelan: 'latin-american', chilean: 'latin-american', guatemalan: 'latin-american',
  caribbean: 'caribbean', antillaise: 'caribbean', caraibes: 'caribbean',
  jamaican: 'caribbean', jamaicaine: 'caribbean', haitian: 'caribbean',
  haitienne: 'caribbean', cuban: 'caribbean', cubaine: 'caribbean', dominican: 'caribbean',
  trinidadian: 'caribbean',
  american: 'american', americaine: 'american', americain: 'american',
  southern: 'american', cajun: 'american', californian: 'american', hawaiian: 'american',
  polish: 'eastern-european', polonaise: 'eastern-european', russian: 'eastern-european',
  russe: 'eastern-european', ukrainian: 'eastern-european', ukrainienne: 'eastern-european',
  hungarian: 'eastern-european', hongroise: 'eastern-european', georgian: 'eastern-european',
  romanian: 'eastern-european', czech: 'eastern-european', ashkenazi: 'eastern-european',
};

/** Words that qualify a cuisine without being one. */
const NOISE = new Set(['inspired', 'style', 'cuisine', 'food', 'inspiree', 'inspire', 'facon', 'a', 'la', 'l', 'de', 'and', 'et']);

/**
 * Map one publisher cuisine string onto the canonical list.
 *
 * Returns null when the string names nothing on the list ("Mediterranean",
 * "Sans gluten…") or names two different cuisines ("American, Indian") — a
 * fusion is for a human to call, not a mapping table.
 */
export function canonicalCuisine(raw: unknown): CanonicalCuisine | null {
  const parts: string[] = Array.isArray(raw)
    ? raw.filter((v): v is string => typeof v === 'string')
    : typeof raw === 'string'
      ? [raw]
      : [];

  const found = new Set<CanonicalCuisine>();
  for (const part of parts.flatMap(p => p.split(/[,;/|&+]/))) {
    let text = ` ${fold(part)} `;
    for (const [phrase, cuisine] of PHRASES) {
      if (text.includes(` ${phrase} `)) {
        found.add(cuisine);
        text = text.replace(` ${phrase} `, ' ');
      }
    }
    for (const word of text.split(' ')) {
      if (!word || NOISE.has(word)) continue;
      const cuisine = WORDS[word];
      if (cuisine) found.add(cuisine);
    }
  }
  return found.size === 1 ? [...found][0] : null;
}
